// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2022 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

/* eslint-disable spire/export-styles */
import TitleTypography from "@hardwoods/App/Components/Common/TitleTypography";
import { pageTitleStyle } from "@hardwoods/XDL/Utilities/SharedStyles";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { getCurrentPage } from "@insite/client-framework/Store/Data/Pages/PageSelectors";
import translate from "@insite/client-framework/Translate";
import { HasFields } from "@insite/client-framework/Types/ContentItemModel";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import Typography from "@insite/mobius/Typography";
import * as React from "react";
import { connect } from "react-redux";
import { css } from "styled-components";

const mapStateToProps = (state: ApplicationState) => ({
    pageTitle: getCurrentPage(state).fields.title,
});

// <XNG-Changes> add customFields
const enum fields {
    widgetTitleStyle = "widgetTitleStyle",
    fontSize = "fontSize",
    fontWeight = "fontWeight",
    margin = "margin",
    overrideTitle = "overrideTitle",
    primaryTitle = "primaryTitle",
    secondaryTitle = "secondaryTitle",
    defaultStyle = "defaultStyle",
}

interface OwnProps extends WidgetProps {
    fields: {
        [fields.widgetTitleStyle]: boolean;
        [fields.fontSize]: string;
        [fields.fontWeight]: string;
        [fields.margin]: string;
        [fields.overrideTitle]: string;
        [fields.primaryTitle]: string;
        [fields.secondaryTitle]: string;
        [fields.defaultStyle]: boolean;
    };
}
type Props = OwnProps & ReturnType<typeof mapStateToProps>;
// </XNG-Changes>

// <XNG-Changes> update style & add customFields
const PageTitle: React.FunctionComponent<Props> = ({ pageTitle: pageTitleProps, fields }) => {
    const pageTitle = fields.overrideTitle || pageTitleProps;
    const cssStyle = css`
        ${fields.defaultStyle
            ? css`
                  ${pageTitleStyle.css}
              `
            : css`
                  margin: ${fields.margin || "0 0 30px 0"};
                  font-size: ${fields.fontSize || "32px"};
                  font-weight: ${fields.fontWeight || "bold"};
              `}
    `;

    if (fields.widgetTitleStyle) {
        return (
            <TitleTypography primaryTitle={fields.primaryTitle} secondaryTitle={fields.secondaryTitle} css={cssStyle} />
        );
    }

    return (
        <Typography data-test-selector="PageTitle" {...pageTitleStyle} css={cssStyle}>
            {pageTitle}
        </Typography>
    );
};

// <XNG-Changes> add customFields
const widgetModule: WidgetModule = {
    component: connect(mapStateToProps)(PageTitle),
    definition: {
        group: "Basic",
        icon: "heading",
        fieldDefinitions: [
            {
                name: fields.defaultStyle,
                editorTemplate: "CheckboxField",
                defaultValue: false,
                fieldType: "General",
            },
            {
                name: fields.widgetTitleStyle,
                displayName: "Widget Title Style",
                editorTemplate: "CheckboxField",
                defaultValue: false,
                fieldType: "General",
            },
            {
                name: fields.overrideTitle,
                displayName: "Override Title",
                editorTemplate: "TextField",
                defaultValue: "",
                fieldType: "General",
                isEnabled: item => !item?.fields.widgetTitleStyle,
            },
            {
                name: fields.primaryTitle,
                displayName: "Primary Title",
                editorTemplate: "TextField",
                defaultValue: "Shop By",
                fieldType: "General",
            },
            {
                name: fields.secondaryTitle,
                displayName: "Secondary Title",
                editorTemplate: "TextField",
                defaultValue: "Project",
                fieldType: "General",
            },
            {
                name: fields.fontSize,
                displayName: "Font Size",
                editorTemplate: "TextField",
                fieldType: "General",
                defaultValue: "32px",
                isRequired: false,
                placeholder: "Enter: 5px or any font-size style",
                validate: (value: string | null, item: HasFields) => {
                    if (!value) {
                        return null;
                    }
                    return CSS.supports("font-size", value) ? null : `Invalid Input`;
                },
                isEnabled: item => !item?.fields.defaultStyle,
            },
            {
                name: fields.fontWeight,
                displayName: "Font Weight",
                editorTemplate: "TextField",
                fieldType: "General",
                defaultValue: "bold",
                isRequired: false,
                placeholder: "Enter: bold or any font-weight style",
                validate: (value: string | null, item: HasFields) => {
                    if (!value) {
                        return null;
                    }
                    return CSS.supports("font-weight", value) ? null : `Invalid Input`;
                },
                isEnabled: item => !item?.fields.defaultStyle,
            },
            {
                name: fields.margin,
                displayName: "Margin Size",
                editorTemplate: "TextField",
                fieldType: "General",
                defaultValue: "0 0 30px 0",
                placeholder: "Enter: 5px or any margin style",
                validate: (value: string | null, item: HasFields) => {
                    if (!value) {
                        return null;
                    }
                    return CSS.supports("margin", value) ? null : `Invalid Input`;
                },
                isEnabled: item => !item?.fields.defaultStyle,
            },
        ],
    },
};

export default widgetModule;
